import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Edit3, ArrowLeft, Send, Printer, CheckCircle, XCircle, AlertCircle, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FillInViewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fillIn, setFillIn] = useState(null);
  const [fillInSets, setFillInSets] = useState([]);
  const [selectedSetIndex, setSelectedSetIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [grading, setGrading] = useState(false);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [displayOrder, setDisplayOrder] = useState([]);

  useEffect(() => {
    loadFillIn();
  }, [id]);

  const shuffleArray = (arr) => {
    const copy = [...arr];
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  const loadFillIn = async () => {
    try {
      const res = await axios.get(`${API}/fill-ins/${id}`);
      setFillIn(res.data);

      const sets = res.data.sets && res.data.sets.length > 0
        ? res.data.sets
        : [res.data.questions || []];
      setFillInSets(sets);

      const firstSet = sets[0];
      setDisplayOrder(shuffleArray(firstSet.map((_, idx) => idx)));

      // Initialize answers
      const initialAnswers = {};
      firstSet.forEach((_, idx) => {
        initialAnswers[idx] = '';
      });
      setAnswers(initialAnswers);
    } catch (error) {
      console.error('Failed to load Fill In:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeQuestions = fillInSets[selectedSetIndex] || [];

  const handleSelectSet = (setIndex) => {
    if (setIndex === selectedSetIndex) return;
    setSelectedSetIndex(setIndex);
    const newSet = fillInSets[setIndex];
    setDisplayOrder(shuffleArray(newSet.map((_, idx) => idx)));
    const initialAnswers = {};
    newSet.forEach((_, idx) => {
      initialAnswers[idx] = '';
    });
    setAnswers(initialAnswers);
    setResults(null);
    setShowResults(false);
  };

  const handleAnswerChange = (questionIdx, value) => {
    setAnswers({
      ...answers,
      [questionIdx]: value
    });
  };

  const handleSubmit = async () => {
    setGrading(true);
    try {
      const res = await axios.post(`${API}/fill-ins/${id}/grade`, {
        user_answers: answers,
        set_index: selectedSetIndex
      });
      setResults(res.data);
      setShowResults(true);
    } catch (error) {
      console.error('Failed to grade answers:', error);
      alert('Failed to grade answers. Please try again.');
    } finally {
      setGrading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleReset = () => {
    const initialAnswers = {};
    activeQuestions.forEach((_, idx) => {
      initialAnswers[idx] = '';
    });
    setAnswers(initialAnswers);
    setResults(null);
    setShowResults(false);
    setDisplayOrder(shuffleArray(activeQuestions.map((_, idx) => idx)));
  };

  const getEvaluationIcon = (evaluation) => {
    if (evaluation === 'CORRECT') return <CheckCircle className="w-6 h-6 text-green-500" />;
    if (evaluation === 'CLOSE') return <AlertCircle className="w-6 h-6 text-yellow-500" />;
    return <XCircle className="w-6 h-6 text-red-500" />;
  };

  const getEvaluationColor = (evaluation) => {
    if (evaluation === 'CORRECT') return 'bg-green-100 border-green-500 text-green-800';
    if (evaluation === 'CLOSE') return 'bg-yellow-100 border-yellow-500 text-yellow-800';
    return 'bg-red-100 border-red-500 text-red-800';
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A90E2] mx-auto"></div>
        </div>
      </Layout>
    );
  }

  if (!fillIn) {
    return (
      <Layout>
        <div className="container mx-auto px-6 py-12 text-center">
          <p className="text-xl text-red-600">Fill In exercise not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <button
          onClick={() => navigate('/fill-in-center')}
          className="flex items-center gap-2 bg-[#2C1810]/90 text-[#F4E4C1] font-bold px-4 py-2 rounded-lg hover:bg-[#2C1810] transition-all mb-6 print:hidden"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Fill In Center</span>
        </button>

        <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-8 rounded-lg mb-8 print:bg-white print:text-black print:border-4 print:border-blue-500">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <Edit3 className="w-12 h-12" />
              <div>
                <h1 className="text-4xl font-bold retro-title">{fillIn.test_title}</h1>
                <p className="text-lg opacity-90">Fill in the Blanks</p>
              </div>
            </div>
            <button
              onClick={handlePrint}
              className="print:hidden bg-white text-blue-600 px-4 py-2 rounded-lg font-bold hover:shadow-lg transition-all flex items-center gap-2 w-full sm:w-auto justify-center"
            >
              <Printer className="w-5 h-5" />
              Print
            </button>
          </div>
        </div>

        {/* Set Selector */}
        {fillInSets.length > 1 && (
          <div className="mb-8 flex flex-wrap gap-2 print:hidden" data-testid="fill-in-set-selector">
            {fillInSets.map((_, setIdx) => (
              <button
                key={setIdx}
                onClick={() => handleSelectSet(setIdx)}
                className={`px-4 py-2 rounded-lg font-bold transition-all ${
                  selectedSetIndex === setIdx
                    ? 'bg-blue-600 text-white scale-105 shadow-lg'
                    : 'bg-[#F4E4C1] text-[#2C1810] hover:bg-[#E8B67A]'
                }`}
                data-testid={`fill-in-set-button-${setIdx}`}
              >
                Set {setIdx + 1}
              </button>
            ))}
          </div>
        )}

        {/* Results Summary */}
        {showResults && results && (
          <div className="bg-gradient-to-br from-blue-100 to-blue-200 border-4 border-blue-500 p-6 rounded-lg mb-8 print:hidden">
            <h2 className="text-3xl font-bold text-blue-800 mb-4 flex items-center gap-2">
              <CheckCircle className="w-8 h-8" />
              Graded by Ernie.AI
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white p-4 rounded-lg text-center">
                <div className="text-4xl font-bold text-blue-600">{results.score}%</div>
                <div className="text-sm text-gray-600">Overall Score</div>
              </div>
              <div className="bg-green-100 p-4 rounded-lg text-center">
                <div className="text-4xl font-bold text-green-600">{results.correct_count}</div>
                <div className="text-sm text-gray-600">Correct</div>
              </div>
              <div className="bg-yellow-100 p-4 rounded-lg text-center">
                <div className="text-4xl font-bold text-yellow-600">{results.close_count}</div>
                <div className="text-sm text-gray-600">Close</div>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 transition-all"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-6" data-testid="fill-in-questions-list">
          {displayOrder.map((idx, displayPos) => {
            const question = activeQuestions[idx];
            const result = results?.results?.find(r => r.question_num === idx + 1);
            const hasResult = showResults && result;

            return (
              <div
                key={idx}
                className={`bg-white border-4 ${
                  hasResult ? getEvaluationColor(result.evaluation).split(' ')[1] : 'border-gray-300'
                } p-6 rounded-lg print:border-2`}
                data-testid={`fill-in-question-${displayPos}`}
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-blue-500 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0">
                    {displayPos + 1}
                  </div>
                  <div className="flex-1">
                    <p className="text-lg font-semibold text-gray-800 mb-4">
                      {question.question_text.split('[BLANK]').map((part, i, arr) => (
                        <span key={i}>
                          {part}
                          {i < arr.length - 1 && (
                            <span className="inline-block mx-2">
                              {question.options && question.options.length > 0 ? (
                                <select
                                  value={answers[idx] || ''}
                                  onChange={(e) => handleAnswerChange(idx, e.target.value)}
                                  disabled={showResults}
                                  className="border-b-2 border-blue-500 px-2 py-1 min-w-[220px] focus:outline-none focus:border-blue-700 disabled:bg-gray-100 print:border-b print:border-black text-[#2C1810] bg-white"
                                  data-testid={`fill-in-select-${idx}`}
                                >
                                  <option value="">Choose an answer...</option>
                                  {question.options.map((opt, oi) => (
                                    <option key={oi} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  type="text"
                                  value={answers[idx] || ''}
                                  onChange={(e) => handleAnswerChange(idx, e.target.value)}
                                  disabled={showResults}
                                  className="border-b-2 border-blue-500 px-2 py-1 min-w-[200px] focus:outline-none focus:border-blue-700 disabled:bg-gray-100 print:border-b print:border-black text-[#2C1810]"
                                  placeholder="Your answer..."
                                />
                              )}
                            </span>
                          )}
                        </span>
                      ))}
                    </p>

                    {/* Show results - Always show for learning even if blank/wrong */}
                    {hasResult && (
                      <div className={`mt-4 p-4 rounded-lg border-2 ${getEvaluationColor(result.evaluation)}`}>
                        <div className="flex items-center gap-2 mb-3">
                          {getEvaluationIcon(result.evaluation)}
                          <span className="font-bold text-lg">{result.evaluation}</span>
                        </div>
                        
                        {/* Always show correct answer for learning */}
                        <div className="mb-3 p-3 bg-green-50 border border-green-300 rounded">
                          <span className="font-semibold text-green-800">✓ Correct Answer: </span>
                          <span className="text-green-900 font-bold">{question.correct_answer}</span>
                        </div>
                        
                        <div className="mb-3">
                          <span className="font-semibold">Your Answer: </span>
                          <span className={`${answers[idx] ? 'text-gray-700' : 'text-red-600 font-semibold'}`}>
                            {answers[idx] || '(No answer provided)'}
                          </span>
                        </div>
                        
                        {/* Explanation - show for wrong/close/blank answers */}
                        {result.evaluation !== 'CORRECT' && (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-300 rounded">
                            <span className="font-semibold text-blue-800 block mb-1">💡 Explanation:</span>
                            <p className="text-sm text-blue-900">{result.feedback}</p>
                          </div>
                        )}
                        
                        {/* Success message for correct answers */}
                        {result.evaluation === 'CORRECT' && (
                          <div className="mt-3 p-3 bg-green-50 border border-green-300 rounded">
                            <span className="font-semibold text-green-800 block mb-1">✓ Great job!</span>
                            <p className="text-sm text-green-900">{result.feedback}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Context (always visible) */}
                    {!showResults && (
                      <div className="mt-3 text-sm text-gray-600 italic print:hidden">
                        <span className="font-semibold">Hint: </span>{question.context}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Submit Button */}
        {!showResults && (
          <div className="mt-8 text-center print:hidden">
            <button
              onClick={handleSubmit}
              disabled={grading}
              className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-12 py-4 rounded-lg font-bold text-xl hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3 mx-auto"
              data-testid="fill-in-submit-button"
            >
              {grading ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  Ernie.AI is grading...
                </>
              ) : (
                <>
                  <Send className="w-6 h-6" />
                  Submit for Grading
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
